import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Analytics } from '@vercel/analytics/react'
import './App.css'
import {
  Beginning,
  Flame,
  FollowUpStory,
  Footer,
  Header,
  IntroStory,
  Manifesto,
  Roadmap,
  Timeline,
  Tokenomics,
} from './components'
import { useIsMobile } from './hooks/useIsMobile'
import { RTL_LANGUAGES } from './i18n'

function App() {
  const { i18n } = useTranslation()
  const [scrollProgress, setScrollProgress] = useState(0)
  const containerRef = useRef(null)
  const isMobile = useIsMobile()
  const lang = i18n.language.split('-')[0]
  const isRtl = RTL_LANGUAGES.includes(lang)

  useEffect(() => {
    document.documentElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr')
    document.documentElement.setAttribute('lang', lang)
  }, [i18n.language])

  useEffect(() => {
    const sectionMap = {
      ['story-of-fire']: '.intro-section',
      tokenomics: '.tokenomics-wrapper',
      roadmap: '.roadmap-wrapper',
      manifesto: '.manifesto-wrapper',
    }
    const hash = window.location.hash.replace('#', '')
    const path = window.location.pathname.replace('/', '')
    const sectionKey = hash || path

    if (!sectionKey || !sectionMap[sectionKey]) return

    if (path && !hash) {
      window.history.replaceState(null, '', `/#${sectionKey}`)
    }

    const scrollToTarget = () => {
      const section = document.querySelector(sectionMap[sectionKey])
      const container = containerRef.current
      if (!section) return
      const top = section.offsetTop - 80
      if (container) container.scrollTop = top
      window.scrollTo(0, top)
    }

    scrollToTarget()
    const scrollRetryShort = setTimeout(scrollToTarget, 50)
    const scrollRetryMedium = setTimeout(scrollToTarget, 200)
    const scrollRetryLong = setTimeout(scrollToTarget, 500)
    return () => {
      clearTimeout(scrollRetryShort)
      clearTimeout(scrollRetryMedium)
      clearTimeout(scrollRetryLong)
    }
  }, [])

  /* Track scroll progress (0–1) for the progress bar and flame scale */
  const handleScroll = (e) => {
    const el = e.currentTarget
    const progress = el.scrollTop / (el.scrollHeight - el.clientHeight)
    setScrollProgress(Math.min(1, Math.max(0, progress)))
  }

  /* Flame scale grows with scroll progress (0.8 → 1.3) */
  const flameScale = 0.8 + scrollProgress * 0.5

  return (
    <>
      <div className='ambient-glow' />
      <Header scrollProgress={scrollProgress} />

      {/* Outside app-container so position:fixed is relative to the viewport, not the scroll container */}
      <div
        className='fixed-timeline-elements'
        style={isMobile ? {
          [isRtl ? 'right' : 'left']: 0,
          transform: 'scale(0.5)',
          transformOrigin: `${isRtl ? 'right' : 'left'} bottom`
        } : undefined}>
        <div
          className='flame-container'
          style={{ transform: `scale(${flameScale})`, transformOrigin: 'center bottom' }}>
          <Flame />
        </div>
        <div className='match-head' />
        <div className='match-stick' />
      </div>

      <div
        className='app-container'
        ref={containerRef}
        onScroll={handleScroll}>
        <div className='beginning-section'>
          <Beginning />
        </div>

        <div className='intro-section' id='story'>
          <IntroStory />
        </div>

        <div className='timeline-section'>
          <Timeline />
        </div>

        <div className='followup-section'>
          <FollowUpStory />
        </div>

        <div className='manifesto-wrapper' id='manifesto'>
          <Manifesto />
        </div>

        <div className='tokenomics-wrapper' id='tokenomics'>
          <Tokenomics />
        </div>

        <div className='roadmap-wrapper' id='roadmap'>
          <Roadmap />
        </div>

        <Footer />
      </div>
      <Analytics />
    </>
  )
}

export default App